'use client';

import { useEffect } from 'react';
import { ATTRIBUTION_COOKIE } from '@/lib/attribution';

// First-touch attribution capture.
//
// Mounted once in the root layout so it covers both marketing pages and auth
// pages (single domain). On first qualifying landing it writes the `pr_attr`
// cookie; on every subsequent visit it does nothing (first-touch wins).
//
// A landing "qualifies" when the URL carries a utm_source OR the referrer is
// external. Portal subdomains (*.planetroadmap.com) are rewritten internally by
// middleware, so they're treated as internal and never counted as a referral.
//
// Everything is wrapped in try/catch — attribution must never break the page.

const COOKIE_MAX_AGE = 30 * 24 * 60 * 60; // 30 days, in seconds

function isInternalReferrer(referrer: string): boolean {
  try {
    const host = new URL(referrer).hostname.toLowerCase();
    // The app's own domain and any portal subdomain (*.planetroadmap.com,
    // rewritten internally) all count as internal.
    return host === 'planetroadmap.com' || host.endsWith('.planetroadmap.com');
  } catch {
    // Unparseable referrer — treat as internal so we don't over-attribute.
    return true;
  }
}

export default function AttributionCapture() {
  useEffect(() => {
    try {
      // First-touch: if the cookie already exists, do nothing.
      if (document.cookie.split(';').some((c) => c.trim().startsWith(`${ATTRIBUTION_COOKIE}=`))) {
        return;
      }

      const params = new URLSearchParams(window.location.search);
      const utmSource = params.get('utm_source') || undefined;
      const referrer = document.referrer || '';
      const hasExternalReferrer = referrer !== '' && !isInternalReferrer(referrer);

      // Only capture when there's a UTM source or a genuine external referrer.
      if (!utmSource && !hasExternalReferrer) {
        return;
      }

      const data = {
        utm_source: utmSource,
        utm_medium: params.get('utm_medium') || undefined,
        utm_campaign: params.get('utm_campaign') || undefined,
        utm_content: params.get('utm_content') || undefined,
        referrer: referrer || undefined,
        landing_page: window.location.href,
        captured_at: new Date().toISOString(),
      };

      const value = encodeURIComponent(JSON.stringify(data));
      document.cookie =
        `${ATTRIBUTION_COOKIE}=${value}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Lax; Secure`;
    } catch {
      // Never let attribution break the page.
    }
  }, []);

  return null;
}
